import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  image?: string;
  keywords?: string;
}

const SITE_NAME = 'DeckPros NJ';
const BASE_URL = 'https://deckprosnj.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;

export const SEO = ({ title, description, canonical, type = 'website', image, keywords }: SEOProps) => {
  const fullTitle = title === 'Home'
    ? `${SITE_NAME} | #1 Deck Builders in New Jersey | Free Estimates`
    : `${title} | ${SITE_NAME}`;
  const url = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

/* ─── LocalBusiness / Organization Schema ─── */
export const LocalBusinessSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': ['HomeAndConstructionBusiness', 'GeneralContractor', 'LocalBusiness'],
    '@id': `${BASE_URL}/#organization`,
    name: 'DeckPros NJ',
    alternateName: 'DeckPros New Jersey',
    description: 'Family-owned premium deck construction, repair, and outdoor living contractor serving New Jersey since 1999. Licensed, bonded, and insured.',
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    image: `${BASE_URL}/og-image.jpg`,
    telephone: '+1-201-657-0855',
    email: 'support@allcompleteconstruction.com',
    foundingDate: '1999',
    slogan: 'Built in America. Built to Last.',
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'NJ',
      addressCountry: 'US',
      addressLocality: 'Morristown',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.697149,
      longitude: -74.826687,
    },
    areaServed: [
      { '@type': 'State', name: 'New Jersey' },
      { '@type': 'AdministrativeArea', name: 'Morris County, NJ' },
      { '@type': 'AdministrativeArea', name: 'Sussex County, NJ' },
      { '@type': 'AdministrativeArea', name: 'Warren County, NJ' },
      { '@type': 'AdministrativeArea', name: 'Hunterdon County, NJ' },
      { '@type': 'AdministrativeArea', name: 'Somerset County, NJ' },
    ],
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '07:00', closes: '18:00' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '08:00', closes: '16:00' },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '500',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'John Smith' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'DeckPros transformed our backyard into the perfect outdoor oasis. Professional, punctual, and exceeded expectations.',
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Sarah Johnson' },
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        reviewBody: 'Best decision we made for our home. Flawless installation and incredible design consultation.',
      },
    ],
    priceRange: '$$',
    paymentAccepted: 'Cash, Check, Credit Card, Financing',
    currenciesAccepted: 'USD',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Deck & Outdoor Living Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Deck Design & Build', description: 'Tailored deck designs built from the ground up.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Deck Repair & Restoration', description: 'Structural repairs, board replacement, resealing.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Deck Replacement', description: 'Full deck replacement with upgraded materials.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Composite & PVC Decking', description: 'Low-maintenance composite and PVC decking install.' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Outdoor Living Spaces', description: 'Pergolas, railings, pavers, full outdoor rooms.' } },
      ],
    },
    sameAs: [
      'https://www.facebook.com/deckprosnj',
      'https://www.instagram.com/deckprosnj',
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/* ─── Breadcrumb Schema ─── */
interface BreadcrumbItem { name: string; url: string; }
export const BreadcrumbSchema = ({ items }: { items: BreadcrumbItem[] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Speakable Schema (AEO — voice search / Google Assistant) ─── */
export const SpeakableSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.section-eyebrow', '[data-speakable]'],
    },
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── FAQ Schema (AEO — answer engines) ─── */
interface FaqItem { question: string; answer: string; }
export const FaqSchema = ({ faqs }: { faqs: FaqItem[] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Service Schema ─── */
export const ServiceSchema = ({ name, description, url }: { name: string; description: string; url: string; }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: `${BASE_URL}${url}`,
    provider: { '@type': 'LocalBusiness', name: 'DeckPros NJ', telephone: '+1-201-657-0855' },
    areaServed: { '@type': 'State', name: 'New Jersey' },
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};
